<template>
  <div id="tipManagement">

    <!-- Title -->
    <PageTitle :title="$t('pos.tip_management.title')" :subtitle="$t('pos.tip_management.description')">
      <div class="pageTitle__buttons">
        <button class="btn btn-primary" @click="showNewTipModal">{{ $t("pos.tip_management.create_entry") }}</button>
      </div>
    </PageTitle>

    <!-- Datatable -->
    <div class="container">
      <Panel :showTitle="false">
        <Datatable ref="datatable" entity="tip_entry" :parameters="parameters" :onRowClick="clickTipEntry"></Datatable>
      </Panel>
    </div>

    <!-- New Tip Modal -->
    <Modal ref="modalNewTip">
      <ModalHeader :title="$t('pos.tip_management.new_tip_entry')" @close="hideNewTipModal"></ModalHeader>
      <div class="modal__description">
        {{ $t('pos.tip_management.new_tip_entry_description') }}
      </div>
      <div class="modal__inputGroup">
        <label for="">{{ $t("pos.tip_management.employee") }}</label>
        <AjaxSelect
          v-model="newTip.employee"
          :api="`/api/entity/employee/select`"
          :map-fn="(i) => `${i.firstName} ${i.lastName}`"
          :allow-empty-search="true"
          :query="{company}"
          :placeholder="$t('search')"
        />
      </div>
      <div class="modal__inputGroup">
        <label for="">{{ $t("sales.amount") }}</label>
        <input type="number" v-model="newTip.amount">
      </div>
      <div class="modal__inputGroup">
        <label for="">{{ $t("date") }}</label>
        <datetime type="datetime" value-zone="local" v-model="newTip.date" />
      </div>
      <div class="modal__actions">
        <div class="actions__back" @click="hideNewTipModal">
          <svgicon src="icon/arrow-left.svg" :width="6" :height="10"></svgicon>
          {{ $t("back") }}
        </div>
        <div class="grow"></div>
        <button class="btn btn-primary" @click="newTipEntry">{{ $t("create") }}</button>
      </div>
    </Modal>

    <!-- Edit Tip Modal -->
    <Modal ref="modalEditTip">
      <ModalHeader :title="$t('pos.tip_management.modify_tip_entry')" @close="hideEditTipModal"></ModalHeader>
      <div class="modal__description">
        {{ $t('pos.tip_management.modify_tip_entry_description') }}
      </div>
      <div class="modal__inputGroup">
        <label for="">{{ $t("pos.tip_management.employee") }}</label>
        <AjaxSelect
          v-model="editTip.employee"
          :api="`/api/entity/employee/select`"
          :map-fn="(i) => `${i.firstName} ${i.lastName}`"
          :allow-empty-search="true"
          :query="{company}"
          :placeholder="$t('search')"
        />
      </div>
      <div class="modal__inputGroup">
        <label for="">{{ $t("sales.amount") }}</label>
        <input type="number" v-model="editTip.amount">
      </div>
      <div class="modal__inputGroup">
        <label for="">{{ $t("date") }}</label>
        <datetime type="datetime" value-zone="local" v-model="editTip.date" />
      </div>
      <div class="modal__actions">
        <div class="actions__back" @click="hideEditTipModal">
          <svgicon src="icon/arrow-left.svg" :width="6" :height="10"></svgicon>
          {{ $t("back") }}
        </div>
        <div class="grow"></div>
        <button class="btn btn-primary" @click="saveTipEntry">{{ $t("save") }}</button>
      </div>
    </Modal>

  </div>
</template>

<script>
  import PageTitle from "@/components/PageTitle.vue";
  import Datatable from "@/components/Datatable.vue";
  import Modal from "@/components/modal/Modal.vue";
  import ModalHeader from "@/components/modal/ModalHeader.vue";
  import { mapActions } from "vuex";
  import moment from "moment";
  import AjaxSelect from "@/components/tools/AjaxSelect";

  export default {

    components: { PageTitle, Datatable, Modal, ModalHeader, AjaxSelect },

    data() {
      return {
        parameters: {
          company: this.$route.query.company
        },
        newTip: {
          employee: {},
          amount: null,
          date: moment().toISOString()
        },
        editTip: {
          id: null,
          employee: {},
          amount: null,
          date: null
        }
      };
    },

    computed: {
      company() {
        return this.$route.query.company;
      },
      currency() {
        return this.$store.state.contextCompany.currency;
      }
    },

    methods: {

      ...mapActions(["getTipEntry", "createTipEntry", "editTipEntry"]),

      showNewTipModal() {
        this.$refs.modalNewTip.show();
      },

      hideNewTipModal() {
        this.$refs.modalNewTip.hide();
      },

      async newTipEntry() {
        // Validate that all fields are filled
        if (!this.newTip.employee.id) {
          toast(this.$t("pos.tip_management.employee_required"), "error");
          return;
        }
        if (!this.newTip.amount) {
          toast(this.$t("pos.tip_management.amount_required"), "error");
          return;
        }
        if (this.newTip.amount <= 0) {
          toast(this.$t("pos.tip_management.amount_positive"), "error");
          return;
        }
        if (parseFloat(this.newTip.amount) != this.newTip.amount) {
          toast(this.$t("pos.tip_management.amount_invalid"), "error");
          return;
        }

        // Create the tip entry
        let result = await this.createTipEntry({
          company: this.company,
          employee: this.newTip.employee.id,
          amount: this.newTip.amount,
          date: this.newTip.date,
          currency: this.currency
        });
        if (!result) {
          toast(this.$t("pos.tip_management.create_error"), "error");
          return;
        }

        // Reset and close the modal
        this.newTip = {
          employee: {},
          amount: null,
          date: moment().toISOString()
        };
        this.hideNewTipModal();
        this.$refs.datatable.reload();
      },

      async clickTipEntry(rowData) {
        let tipEntry = await this.getTipEntry({
          company: this.company,
          tipId: rowData.id
        });
        this.editTip.id = tipEntry.id;
        this.editTip.employee = tipEntry.employee;
        this.editTip.amount = tipEntry.amount;
        this.editTip.date = moment(tipEntry.date).format("YYYY-MM-DDTHH:mm:ss");
        this.$refs.modalEditTip.show();
      },

      hideEditTipModal() {
        this.$refs.modalEditTip.hide();
      },

      async saveTipEntry() {
        // Validate that all fields are filled
        if (!this.editTip.employee.id) {
          toast(this.$t("pos.tip_management.employee_required"), "error");
          return;
        }
        if (!this.editTip.amount) {
          toast(this.$t("pos.tip_management.amount_required"), "error");
          return;
        }
        if (this.editTip.amount <= 0) {
          toast(this.$t("pos.tip_management.amount_positive"), "error");
          return;
        }
        if (parseFloat(this.editTip.amount) != this.editTip.amount) {
          toast(this.$t("pos.tip_management.amount_invalid"), "error");
          return;
        }

        // Edit the tip entry
        let result = await this.editTipEntry({
          tipId: this.editTip.id,
          company: this.company,
          employee: this.editTip.employee.id,
          amount: this.editTip.amount,
          date: this.editTip.date,
          currency: this.currency
        });
        if (!result) {
          toast(this.$t("pos.tip_management.edit_error"), "error");
          return;
        }

        // Reset and close the modal
        this.editTip = {
          id: null,
          employee: {},
          amount: null,
          date: null
        };
        this.hideEditTipModal();
        this.$refs.datatable.reload();
      }

    }

  };
</script>

<style lang="scss" scoped>
  .pageTitle__buttons {
    .btn:not(:last-child) {
      margin-right: 10px;
    }
  }

  .modal__description {
    margin-bottom: 32px;
  }

  .modal__inputGroup {
    margin-bottom: 16px;
    label {
      margin-bottom: 4px;
    }
    input {
      width: 100%;
    }
  }

  .modal__actions {
    display: flex;
    align-items: center;
    .grow {
      flex-grow: 1;
    }
    .actions__back {
      display: flex;
      align-items: center;
      color: var(--primary-color);
      fill: var(--primary-color);
      cursor: pointer;
      .svgicon {
        margin-right: 8px;
      }
    }
  }
</style>

<style lang="scss">
  .newmodal .modal__inputGroup input {
    width: 100%;
  }
</style>